import {
  BillingScheduleRespStatusEnum,
  IBillingSchedulePeriodRespSchemaUI,
} from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import {
  AppColumnDef,
  ExpandedState,
  createColumnHelper,
} from '../../../../components/Monetize/DataTableNew/appTable';
import { useCallback, useMemo, useState } from 'react';
import { MdArrowDropDown, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { doPrintInvoiceToPdf } from '../../../../api/accountsService';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MIcon,
  MSkeleton,
  MText,
} from '../../../../components/Monetize';
import DataTableActions from '../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { InvoiceLookupPopover } from '../../../../components/Monetize/LookupPopover/InvoiceLookupPopover';
import { MEntityStatusBadge } from '../../../../components/Monetize/MEntityStatusBadge';
import PreviewPdfDrawer from '../../../../components/Monetize/PreviewPdfDrawer/PreviewPdfDrawer';
import { BillingScheduleStatusEnumDiplay } from '../../../../constants/billGroups';
import { InvoiceItemsTable } from './InvoiceItemsTable';

const columnHelper = createColumnHelper<IBillingSchedulePeriodRespSchemaUI>();

interface InvoicesUnderContractTableProps {
  periods?: IBillingSchedulePeriodRespSchemaUI[];
  billGroupCurrency: string;
  billingScheduleLoading: boolean;
}

export const InvoicesUnderContractTable = ({
  periods,
  billGroupCurrency,
  billingScheduleLoading,
}: InvoicesUnderContractTableProps) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [pdfInvoiceId, setPdfInvoiceId] = useState<string | null>(null);

  const isExpandedAll = useMemo(() => {
    if (!periods?.length) {
      return false;
    }
    if (expanded === true) {
      return true;
    }
    if (typeof expanded === 'object') {
      return periods.every(({ uid }) => expanded[uid]);
    }
    return false;
  }, [periods, expanded]);

  const fetchDocument = useCallback(async (): Promise<ArrayBuffer | null> => {
    if (pdfInvoiceId) {
      const { data } = await doPrintInvoiceToPdf(pdfInvoiceId, null);
      return data;
    }
    return null;
  }, [pdfInvoiceId]);

  const actionBodyTemplate = useCallback(
    (data: IBillingSchedulePeriodRespSchemaUI) => {
      const actions = [
        {
          title: 'View Invoice PDF',
          enabled: !!data.invoiceId,
          action: () => {
            setPdfInvoiceId(data.invoiceId);
          },
        },
      ];
      return <DataTableActions actions={actions} />;
    },
    [],
  );

  const columns: AppColumnDef<IBillingSchedulePeriodRespSchemaUI, any>[] =
    useMemo(
      () => [
        columnHelper.accessor('invoiceId', {
          header: 'Name',
          size: 300,
          cell: ({ row }) => {
            const data = row.original;
            const isExpanded = row.getIsExpanded();
            return (
              <MFlex gap={2} align="center">
                <MCustomIconButton
                  icon={isExpanded ? MdExpandLess : MdExpandMore}
                  btnSize={5}
                  onClick={(event) => {
                    event.stopPropagation();
                    row.toggleExpanded();
                  }}
                  _focus={{ bg: 'transparent' }}
                  aria-label={
                    isExpanded
                      ? `Collapse row ${row.id}`
                      : `Expand row ${row.id}`
                  }
                />
                {data.invoiceId ? (
                  <InvoiceLookupPopover value={data.invoiceId} />
                ) : (
                  <MText>Upcoming Invoice</MText>
                )}
              </MFlex>
            );
          },
        }),
        columnHelper.accessor('servicePeriod', {
          header: 'Service Period',
          size: 200,
          cell: ({ getValue }) => {
            const { start, end } = getValue();
            return (
              <MText>
                {toDateShort(start)} - {toDateShort(end)}
              </MText>
            );
          },
        }),
        columnHelper.accessor('invoiceDate', {
          header: 'Invoice Date',
          size: 200,
          cell: ({ getValue }) => <MText>{toDateShort(getValue())}</MText>,
        }),
        columnHelper.accessor('amount', {
          header: 'Amount',
          size: 140,
          cell: ({ getValue, row }) => {
            const { usageProductExists, status } = row.original;
            const formattedAmount = formatCurrency(getValue(), {
              currency: billGroupCurrency,
            });
            return (
              <MText>
                {formattedAmount}
                {usageProductExists &&
                  status !== BillingScheduleRespStatusEnum.INVOICED &&
                  '*'}
              </MText>
            );
          },
        }),
        columnHelper.accessor('status', {
          header: 'Status',
          size: 120,
          cell: ({ row }) => (
            <MEntityStatusBadge
              status={BillingScheduleStatusEnumDiplay[row.original.status]}
              ml="0"
            />
          ),
        }),
        createActionColumn<IBillingSchedulePeriodRespSchemaUI>(
          actionBodyTemplate,
        ),
      ],
      [actionBodyTemplate, billGroupCurrency],
    );

  if (billingScheduleLoading || !periods) {
    return (
      <MBox p={4}>
        <MSkeleton height={10} w="full" />
      </MBox>
    );
  }

  function handleToggleExpandAll() {
    if (isExpandedAll) {
      setExpanded({});
    } else {
      setExpanded(true);
    }
  }

  return (
    <>
      <MBox justifySelf="flex-start">
        {periods.length > 1 && (
          <MButton
            variant="tertiary"
            size="sm"
            ml={2}
            rightIcon={
              <MIcon
                as={MdArrowDropDown}
                w="5"
                h="5"
                transform={isExpandedAll ? 'rotate(180deg)' : undefined}
                transition="all ease 0.3s"
              />
            }
            onClick={handleToggleExpandAll}
          >
            {isExpandedAll ? 'Collapse all' : 'Expand all'}
          </MButton>
        )}
      </MBox>
      <DataTable
        columns={columns}
        data={periods}
        clientControlled
        skipFilterValuePreprocessing
        enablePagination={false}
        containerProps={{ pl: 4, maxHeight: 'none' }}
        extraProps={{ containerProps: { overflowY: 'visible' } }}
        getRowId={(row) => row.uid}
        onRowClick={(_row: IBillingSchedulePeriodRespSchemaUI, tableRow) => {
          tableRow.toggleExpanded();
        }}
        state={{ expanded }}
        onExpandedChange={setExpanded}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        customChildRow={{
          getRowCanExpand: () => true,
          renderCustomExpandedRow: (row) => (
            <InvoiceItemsTable period={row.original} />
          ),
        }}
      />

      {!!pdfInvoiceId && (
        <PreviewPdfDrawer
          filename={`invoice-${pdfInvoiceId}`}
          isOpen={!!pdfInvoiceId}
          fetchDocument={fetchDocument}
          onClose={() => setPdfInvoiceId(null)}
        />
      )}
    </>
  );
};
